/**
 * Filename helpers for the document burn-in flow (VE-20837).
 *
 * The ticket spec for the resulting `redacted-document` asset filename
 * (with the version suffix added later for parity with imageRedactionSaga's
 * `${name}[REDACTED].v${version}.jpeg` convention):
 *   - When redactions are present:
 *       `court case 123.pdf` -> `court case 123[REDACTED].v1.pdf`
 *       `court case 123.doc` -> `court case 123.doc[REDACTED].v1.pdf`
 *   - When the user confirmed an empty redaction set:
 *       `court case 123.pdf` -> `court case 123[NO_REDACTIONS].v1.pdf`
 *       `court case 123.doc` -> `court case 123.doc[NO_REDACTIONS].v1.pdf`
 *
 * The behavior is:
 *   - If the source name ends in `.pdf` (case-insensitive), drop the
 *     trailing `.pdf`, append `<tag>.v<version>.pdf`.
 *   - Otherwise keep the original extension intact and append
 *     `<tag>.v<version>.pdf` (the burn-in always produces a PDF
 *     regardless of the source).
 *   - If the source has no extension at all, just append the tag +
 *     version + `.pdf`.
 *
 * There is a SECOND convention here, for the merged PDF a request
 * releases: `<name>[MERGED][REDACTED].<YYYYMMDD>.v<N>.pdf`. The two share
 * their tag vocabulary on purpose, because on disk they sit side by side.
 * Why the tags are shaped the way they are is at `MERGED_TAG`; how the merged
 * name is assembled is at `buildMergedDocumentFilename`.
 *
 * The one difference worth knowing up here is the BASE. A single file's name
 * came from an upload and was already a legal filename; a request's name was
 * typed by a person and routinely holds `/` and `:`, so only the merged path
 * sanitises (`sanitizeRequestName`).
 */

const REDACTED_TAG = '[REDACTED]';
const NO_REDACTIONS_TAG = '[NO_REDACTIONS]';
/**
 * ITS OWN BRACKET, before the redaction one.
 */
const MERGED_TAG = '[MERGED]';

export function buildRedactedFilename(
  sourceName: string,
  hasRedactions: boolean,
  version: number
): string {
  const tag = hasRedactions ? REDACTED_TAG : NO_REDACTIONS_TAG;
  // Negative / non-integer versions never come from the saga in practice but
  // we still want a sensible filename if a caller passes something odd.
  const safeVersion =
    Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
  const suffix = `${tag}.v${safeVersion}.pdf`;
  const trimmed = sourceName.trim();
  if (!trimmed) {
    return suffix;
  }
  // Case-insensitive .pdf suffix check; preserve the original casing of the
  // base name so `Court Case.PDF` -> `Court Case[REDACTED].v1.pdf` keeps the base.
  if (/\.pdf$/i.test(trimmed)) {
    const base = trimmed.slice(0, -4);
    return `${base}${suffix}`;
  }
  return `${trimmed}${suffix}`;
}

/**
 * the merged document's filename:
 * `[Name][MERGED][REDACTED].[YYYYMMDD].v[N].pdf`.
 */
export function buildMergedDocumentFilename(
  name: string,
  options: {
    /**
     * Include the `[MERGED]` tag. A stored CHOICE rather than an inference
     * from whether the name was edited: inferring it meant a reviewer could
     * only drop the word by renaming, and could not keep it if they wanted a
     * name of their own — two decisions welded to one control.
     */
    readonly appendMerged: boolean;
    /**
     * Include the `.YYYYMMDD.` segment. Optional because the version still
     * disambiguates without it, so the cost of turning it off is legibility
     * rather than collisions — nobody looking at `v3` knows whether it is from
     * this morning or from March.
     */
    readonly includeDate: boolean;
    /**
     * Did ANY member contribute a redaction? False only when every member's
     * output says it removed nothing; an unknown member counts as redacted,
     * because `[NO_REDACTIONS]` is a claim about the whole release and a
     * claim nobody can check must not be made.
     */
    readonly hasRedactions: boolean;
    readonly version: number;
  },
  now: Date = new Date()
): string {
  const { appendMerged, includeDate, hasRedactions, version } = options;
  const safeVersion =
    Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const base = sanitizeRequestName(name) || 'Request';
  const merged = appendMerged ? MERGED_TAG : '';
  const tag = hasRedactions ? REDACTED_TAG : NO_REDACTIONS_TAG;
  const date = includeDate ? `.${yyyy}${mm}${dd}` : '';
  return `${base}${merged}${tag}${date}.v${safeVersion}.pdf`;
}

/**
 * Make a user-typed request name safe to use as a filename. Reserved
 * characters become `_`; trailing dots and spaces are STRIPPED, because
 * Windows removes them silently and two names would collide.
 */
function sanitizeRequestName(name: string): string {
  return (
    name
      .trim()
      .replace(/[/\\?%*:|"<>]/g, '_')
      // Control characters are legal in neither a filename nor a header.
      // Filtered by code point rather than matched by a regex range, which
      // eslint rejects (`no-control-regex`) for the good reason that an
      // invisible character inside a pattern cannot be reviewed.
      .split('')
      .filter((ch) => {
        const code = ch.charCodeAt(0);
        return code > 0x1f && code !== 0x7f;
      })
      .join('')
      .replace(/\s+/g, ' ')
      .replace(/[. ]+$/, '')
      .slice(0, 120)
  );
}
